import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Chip,
} from '@mui/material';
import {
  Hotel,
  Close,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { LoyaltyProgram } from '../../types';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
  background: theme.palette.mode === 'light' 
    ? 'rgba(255, 255, 255, 0.8)'
    : 'rgba(15, 23, 42, 0.8)',
  backdropFilter: 'blur(10px)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.palette.mode === 'light'
      ? '0 12px 30px rgba(0, 0, 0, 0.1)'
      : '0 12px 30px rgba(0, 0, 0, 0.3)',
  },
}));

interface Props {
  program: LoyaltyProgram;
  onDelete: (program: LoyaltyProgram) => void;
}

const HotelProgramDisplay: React.FC<Props> = ({ program, onDelete }) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleDelete = () => {
    onDelete(program);
    setDeleteDialogOpen(false);
  };

  // Hotel chain colors mapping
  const getHotelChainColor = (name: string): string => {
    const colors: Record<string, string> = {
      'marriott': '#C41E3A',
      'hilton': '#003F7F', 
      'hyatt': '#8B4B9C',
      'ihg': '#006747',
      'accor': '#FF6B35',
      'choice': '#2E5A87',
      'wyndham': '#F47421',
      'best western': '#004B87',
      'radisson': '#C8102E',
      'four seasons': '#8B4513'
    };
    
    const lowerName = name.toLowerCase();
    for (const [key, color] of Object.entries(colors)) {
      if (lowerName.includes(key)) {
        return color;
      }
    }
    return '#ec4899'; // Default hotel pink
  };

  const hotelColor = getHotelChainColor(program.name);

  return (
    <>
      <StyledCard
        sx={{
          background: (theme) => theme.palette.mode === 'dark'
            ? `linear-gradient(135deg, 
                rgba(255, 255, 255, 0.08) 0%, 
                rgba(255, 255, 255, 0.04) 25%, 
                rgba(255, 255, 255, 0.02) 50%, 
                rgba(255, 255, 255, 0.04) 75%, 
                rgba(255, 255, 255, 0.08) 100%), 
                linear-gradient(135deg, 
                ${hotelColor}60 0%, 
                ${hotelColor}40 25%, 
                ${hotelColor}30 50%, 
                ${hotelColor}40 75%, 
                ${hotelColor}60 100%)`
            : `linear-gradient(135deg, 
                ${hotelColor}20 0%, 
                ${hotelColor}10 25%, 
                ${hotelColor}05 50%, 
                ${hotelColor}10 75%, 
                ${hotelColor}20 100%)`,
          backdropFilter: 'blur(20px)',
          border: (theme) => theme.palette.mode === 'dark' 
            ? `2px solid ${hotelColor}80`
            : `1px solid ${hotelColor}30`,
          boxShadow: (theme) => theme.palette.mode === 'dark'
            ? `0 4px 20px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)`
            : 'none',
          color: 'text.primary',
          position: 'relative',
          overflow: 'hidden',
          minHeight: '160px',
          cursor: 'pointer',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: (theme) => theme.palette.mode === 'dark'
              ? `0 8px 30px rgba(0, 0, 0, 0.4), 0 0 20px ${hotelColor}40, inset 0 1px 0 rgba(255, 255, 255, 0.15)`
              : `0 8px 25px -4px ${hotelColor}40`,
            border: (theme) => theme.palette.mode === 'dark'
              ? `2px solid ${hotelColor}`
              : `1px solid ${hotelColor}50`,
            '& .delete-button': {
              opacity: 1,
            },
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            right: '-30%',
            width: '120px',
            height: '120px',
            background: (theme) => theme.palette.mode === 'dark'
              ? `radial-gradient(circle, ${hotelColor}40 0%, transparent 70%)`
              : `radial-gradient(circle, ${hotelColor}15 0%, transparent 70%)`,
            borderRadius: '50%',
            filter: 'blur(10px)'
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '-30%',
            left: '-20%',
            width: '80px',
            height: '80px',
            background: (theme) => theme.palette.mode === 'dark'
              ? `radial-gradient(circle, ${hotelColor}30 0%, transparent 60%)`
              : `radial-gradient(circle, ${hotelColor}10 0%, transparent 60%)`,
            borderRadius: '50%',
            filter: 'blur(8px)'
          }
        }}
      >
        <CardContent sx={{ 
          position: 'relative', 
          zIndex: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: 2
        }}>
          {/* Delete Button */}
          <IconButton
            className="delete-button"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteDialogOpen(true);
            }}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 6,
              width: 28,
              height: 28,
              backgroundColor: (theme) => theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.15)' 
                : 'rgba(0, 0, 0, 0.1)',
              backdropFilter: 'blur(10px)',
              border: (theme) => theme.palette.mode === 'dark' 
                ? '1px solid rgba(255, 255, 255, 0.4)' 
                : '1px solid rgba(255, 255, 255, 0.2)',
              color: (theme) => theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.8)' 
                : 'text.secondary',
              opacity: 0,
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(239, 68, 68, 0.2)',
                color: 'error.main',
                transform: 'scale(1.1)',
                border: '1px solid rgba(239, 68, 68, 0.5)',
              },
            }}
          >
            <Close sx={{ fontSize: 16 }} />
          </IconButton>

          {/* Header with Logo */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 700,
              fontSize: '0.9rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              color: (theme) => theme.palette.mode === 'dark' 
                ? '#ffffff' 
                : 'text.primary',
              textShadow: (theme) => theme.palette.mode === 'dark'
                ? '0 1px 2px rgba(0,0,0,0.7)'
                : 'none'
            }}>
              {program.name}
            </Typography>
            <Box sx={{
              background: (theme) => theme.palette.mode === 'dark'
                ? `linear-gradient(135deg, 
                    rgba(255, 255, 255, 0.15) 0%, 
                    rgba(255, 255, 255, 0.05) 100%), 
                    linear-gradient(135deg, 
                    ${hotelColor}70, 
                    ${hotelColor}90)`
                : `linear-gradient(135deg, ${hotelColor}20, ${hotelColor}40)`,
              backdropFilter: 'blur(10px)',
              border: (theme) => theme.palette.mode === 'dark'
                ? `1px solid ${hotelColor}`
                : `1px solid ${hotelColor}50`,
              borderRadius: '50%',
              p: 0.8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: (theme) => theme.palette.mode === 'dark'
                ? `0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)`
                : 'none'
            }}>
              <Hotel sx={{ 
                color: (theme) => theme.palette.mode === 'dark' 
                  ? '#ffffff' 
                  : hotelColor, 
                fontSize: 16 
              }} />
            </Box>
          </Box>

          {/* Tier Badge */}
          <Box sx={{ mb: 1 }}>
            <Chip 
              label={program.tier} 
              size="small"
              sx={{ 
                backgroundColor: (theme) => theme.palette.mode === 'dark'
                  ? `rgba(255, 255, 255, 0.12)`
                  : `${hotelColor}20`,
                backdropFilter: 'blur(10px)',
                border: (theme) => theme.palette.mode === 'dark'
                  ? `1px solid rgba(255, 255, 255, 0.25)`
                  : `1px solid ${hotelColor}30`,
                color: (theme) => theme.palette.mode === 'dark'
                  ? '#ffffff'
                  : hotelColor,
                fontWeight: 600,
                fontSize: '0.7rem',
                height: '22px',
                boxShadow: (theme) => theme.palette.mode === 'dark'
                  ? 'inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                  : 'none',
                '& .MuiChip-label': {
                  color: (theme) => theme.palette.mode === 'dark'
                    ? '#ffffff'
                    : hotelColor
                }
              }}
            />
          </Box>

          {/* Points */}
          <Typography variant="h5" sx={{ 
            fontWeight: 800,
            fontSize: '1.3rem',
            color: (theme) => theme.palette.mode === 'dark' 
              ? '#ffffff' 
              : 'text.primary',
            textShadow: (theme) => theme.palette.mode === 'dark'
              ? '0 1px 2px rgba(0,0,0,0.7), 0 0 8px rgba(0,0,0,0.3)'
              : '0 1px 2px rgba(0,0,0,0.1)'
          }}>
            {program.points?.toLocaleString() || '0'}
          </Typography>
          <Typography variant="body2" sx={{ 
            fontSize: '0.8rem',
            color: (theme) => theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.8)' 
              : 'text.secondary',
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            textShadow: (theme) => theme.palette.mode === 'dark'
              ? '0 1px 2px rgba(0,0,0,0.5)'
              : 'none'
          }}>
            {program.currency || 'POINTS'}
          </Typography>
        </CardContent>
      </StyledCard>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Hotel Program</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {program.name}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HotelProgramDisplay;